import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import dayjs from 'dayjs';
import { camelize, camelizeKeys } from 'humps';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import {
  PLATFORM_MEDIA_TYPES,
  SENTIMENT_DISALLOWED_CHANNELS,
  SENTIMENT_SOURCE_TYPES,
  SENTIMENTS,
  sourceLists,
  sourceTypeEnum,
  VISION_PREDICTIONS,
  visualSources,
} from '@/app/library/constants';
import { useMediaDrawer } from '@/composables/useMediaDrawer';
import { constants, SOURCE_TO_METRIC_PROP } from '@/config';
import { BRAND } from '@/models/auth/permissions.enum';
import { useIdentityStore } from '@/stores/identity';
import {
  getMediaVideoDuration,
  isMediaAudio,
  isMediaDocument,
  isMediaPromoted,
  isMediaText,
  isMediaVideo,
  resolvePlatformMediaType,
} from '@/utils/media';
import { getUserTimezone } from '@/utils/timezone';
import { engagementRating, showPredictions } from '@/utils/vision';

export function useMediaData(media, mediaSource = null, patchJson = null) {
  const router = useRouter();
  const identityStore = useIdentityStore();

  const camelCaseMedia = computed(() => camelizeKeys(media));

  const channelKey = computed(() => {
    if (!media?.source) return null;
    return SOURCE_TO_METRIC_PROP[media.source] || media.source?.toLowerCase();
  });

  const channelName = computed(() => {
    if (channelKey.value === constants.INSTAGRAM_STORY_FRAME.toLowerCase()) {
      return constants.INSTAGRAM;
    }
    return channelKey.value;
  });

  const sourceData = computed(() => {
    const camelCaseMediaValue = camelCaseMedia.value;
    const formattedChannelKey = camelize(channelKey.value);
    const formattedSourceData = {
      ...camelCaseMediaValue,
      ...camelCaseMediaValue[formattedChannelKey],
    };

    if (channelKey.value === constants.INSTAGRAM.toLowerCase()) {
      formattedSourceData.likeshopClicks = camelCaseMediaValue?.likeshop?.clicks;
    }
    return formattedSourceData;
  });

  const mediaSrc = computed(() => {
    const camelCaseMediaValue = camelCaseMedia.value;
    const isVideo = isMediaVideo(camelCaseMediaValue);
    const imageSizes = camelCaseMediaValue?.image?.sizes;
    const mediaImages = isVideo ? camelCaseMediaValue?.video?.thumbnails || imageSizes : imageSizes;
    if (!mediaImages) {
      return camelCaseMediaValue.thumb || '';
    }
    return (
      mediaImages.small?.url || mediaImages.smallSquare?.url || camelCaseMediaValue.thumb || ''
    );
  });

  const mediaType = computed(() => {
    if (isMediaVideo(camelCaseMedia.value)) {
      return 'video';
    }
    if (isMediaText(camelCaseMedia.value)) {
      return 'text';
    }
    if (isMediaDocument(camelCaseMedia.value)) {
      return 'document';
    }
    if (isMediaAudio(camelCaseMedia.value)) {
      return 'audio';
    }
    return 'image';
  });

  const platformMediaType = computed(() => resolvePlatformMediaType(media));

  const mediaDuration = computed(() => {
    if (mediaType.value !== 'video') return undefined;
    return getMediaVideoDuration(camelCaseMedia.value);
  });

  const mediaText = computed(() => {
    const isXLinkPost =
      channelKey.value === constants.TWITTER.toLowerCase() &&
      platformMediaType.value === PLATFORM_MEDIA_TYPES.LINK;

    if (mediaType.value !== 'text' && !isXLinkPost) return undefined;

    if (channelKey.value === constants.LINKEDIN.toLowerCase())
      return sourceData.value?.[channelKey.value]?.caption;

    if (channelKey.value === constants.FACEBOOK.toLowerCase())
      return sourceData.value?.[channelKey.value]?.message;

    return sourceData.value?.[channelKey.value]?.text;
  });

  const mediaDate = computed(() => {
    const date = camelCaseMedia.value.sourceCreatedAt;
    if (!date) return null;
    return dayjs.utc(date).tz(getUserTimezone()).toDate();
  });

  const shouldShowSentiment = computed(() => {
    if (!identityStore.guard(BRAND.GENERAL.CAN_ACCESS_SENTIMENT)) return false;

    const sourceType = camelCaseMedia.value.sourceType;
    const isTikTokUGCSource =
      channelKey.value === constants.TIKTOK.toLowerCase() && sourceType === sourceTypeEnum.UGC;
    const hasADisallowedPlatformAndSource =
      SENTIMENT_DISALLOWED_CHANNELS.includes(channelKey.value) || isTikTokUGCSource;
    const hasAPermittedSourceType = SENTIMENT_SOURCE_TYPES.includes(sourceType);

    return hasAPermittedSourceType && !hasADisallowedPlatformAndSource;
  });

  const mediaSentiment = computed(() => {
    if (!shouldShowSentiment.value) return '';

    const sentimentType =
      camelCaseMedia.value.sourceType === sourceTypeEnum.OWNED
        ? 'commentSentiment'
        : 'captionSentiment';
    const sentiment = camelCaseMedia.value[sentimentType];

    if (sentiment?.isPositive) return SENTIMENTS.POSITIVE;
    if (sentiment?.isNegative) return SENTIMENTS.NEGATIVE;
    if (sentiment?.isNeutral) return SENTIMENTS.NEUTRAL;
    return '';
  });

  const mediaCaption = computed(() => {
    const data = sourceData.value;
    return data?.message || data?.caption || data?.title || data?.text || data?.meta?.filename;
  });

  const mediaAvatar = computed(() => camelCaseMedia.value?.creator?.avatarUrl ?? null);

  const mediaHandle = computed(
    () =>
      camelCaseMedia.value?.creator?.handle ??
      sourceData.value?.instagramUser?.handle ??
      sourceData.value?.twitterUser?.handle ??
      sourceData.value?.facebookUser?.handle ??
      null,
  );

  const visionPrediction = computed(() => {
    const { source, sourceType } = sourceData.value || {};
    const sourceTypeValue = [
      visualSources.UPLOADED,
      visualSources.UPLOAD,
      visualSources.AI_GENERATION,
    ].includes(source)
      ? source
      : `${source}_${sourceType}`;

    if (!showPredictions({ ...sourceData.value, sourceType: sourceTypeValue }, router)) return null;

    const prediction = sourceData.value?.predictions?.engagement;
    if (!prediction) return null;
    if (prediction > engagementRating.excellent) return VISION_PREDICTIONS.TOP;
    if (prediction > engagementRating.good) return VISION_PREDICTIONS.BETTER;
    return VISION_PREDICTIONS.LESS;
  });

  const contentTags = computed(() => {
    const canAccessContentTags = identityStore.guard(BRAND.TAGS.CAN_ACCESS_CONTENT_TAGS);
    return canAccessContentTags ? sourceData.value?.contentTags : null;
  });

  const isPromoted = computed(() => isMediaPromoted(camelCaseMedia.value));

  const isUGC = computed(() => {
    const m = camelCaseMedia.value;
    if (m?.isUgc === true) return true;
    const st = m?.sourceType;
    if (typeof st === 'string' && sourceLists.UGC_LIST.includes(st)) return true;
    const bmt = m?.brandMediaType;
    if (typeof bmt === 'string') {
      if (sourceLists.UGC_LIST.includes(bmt)) return true;
      if (bmt.includes('_UGC')) return true;
    }
    return false;
  });

  const isVisionAiGenerated = computed(
    () => sourceData.value?.source === visualSources.AI_GENERATION,
  );

  const isSnapchatStory = computed(() =>
    ['STORY', 'SAVED_STORY'].includes(sourceData.value?.snapchat?.assetType),
  );

  const { openMedia } = useMediaDrawer();
  const { launchDrawer } = useDrawer();
  async function openMediaDrawer() {
    if (isSnapchatStory.value) {
      void launchDrawer({
        name: 'snapchatStory',
        props: {
          mediaId: camelCaseMedia.value.id,
          patchJson,
        },
      });
    } else {
      await openMedia({
        id: camelCaseMedia.value.id,
        brandId: camelCaseMedia.value.brandId,
        source: mediaSource,
        patchJson,
      });
    }
  }

  return {
    channelKey,
    channelName,
    sourceData,
    mediaSrc,
    mediaType,
    platformMediaType,
    mediaText,
    mediaDate,
    mediaDuration,
    mediaCaption,
    mediaAvatar,
    mediaHandle,
    mediaSentiment,
    contentTags,
    visionPrediction,
    isPromoted,
    isUGC,
    isVisionAiGenerated,
    openMediaDrawer,
  };
}
